import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Scissors, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export default function HairCuttingDays() {
    const [year, setYear] = useState(2026);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);

    useEffect(() => {
        fetchData();
    }, [year, month]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/tong-shu/hair-cutting/${year}/${month}`);
            const json = await res.json();
            setData(json);
        } catch (err) {
            console.error('Failed to fetch hair cutting days:', err);
        }
        setLoading(false);
    };

    const prevMonth = () => {
        if (month === 1) {
            setMonth(12);
            setYear(year - 1);
        } else {
            setMonth(month - 1);
        }
        setSelectedDay(null);
    };

    const nextMonth = () => {
        if (month === 12) {
            setMonth(1);
            setYear(year + 1);
        } else {
            setMonth(month + 1);
        }
        setSelectedDay(null);
    };

    const getDayInfo = (dayNum) => {
        if (!data) return null;
        return data.days.find(d => d.day === dayNum);
    };

    // Generate calendar grid
    const renderCalendar = () => {
        if (!data) return null;

        const firstDay = data.first_weekday; // 0 = Monday
        const daysInMonth = data.days_in_month;
        const weeks = [];
        let currentWeek = [];

        // Add empty cells for days before the 1st
        for (let i = 0; i < firstDay; i++) {
            currentWeek.push(null);
        }

        // Add days
        for (let day = 1; day <= daysInMonth; day++) {
            currentWeek.push(day);
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
        }

        // Fill remaining cells
        while (currentWeek.length < 7 && currentWeek.length > 0) {
            currentWeek.push(null);
        }
        if (currentWeek.length > 0) {
            weeks.push(currentWeek);
        }

        return weeks;
    };

    const weeks = renderCalendar();

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <Scissors className="w-8 h-8 text-emerald-600" />
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
                            Hair Cutting Days
                        </h1>
                    </div>
                    <p className="text-slate-500">理髮吉日 - Good & Bad Days to Cut Hair</p>
                </div>

                {/* Month Navigation */}
                <div className="flex items-center justify-center gap-4 mb-6">
                    <Button variant="outline" size="icon" onClick={prevMonth}>
                        <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <h2 className="text-2xl font-bold text-slate-800 min-w-[200px] text-center">
                        {MONTH_NAMES[month - 1]} {year}
                    </h2>
                    <Button variant="outline" size="icon" onClick={nextMonth}>
                        <ChevronRight className="w-5 h-5" />
                    </Button>
                </div>

                {/* Legend */}
                <div className="flex justify-center gap-6 mb-6">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-emerald-500 rounded flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-slate-600">Good Day ({data?.good_count || 0})</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-red-500 rounded flex items-center justify-center">
                            <X className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-slate-600">Bad Day ({data?.bad_count || 0})</span>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-slate-500">Loading...</p>
                    </div>
                ) : (
                    <>
                        {/* Calendar Grid */}
                        <Card className="mb-6">
                            <CardContent className="p-4">
                                {/* Weekday Headers */}
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                        <div key={day} className="text-center font-semibold text-slate-500 text-sm py-2">
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                {/* Calendar Days */}
                                {weeks && weeks.map((week, weekIndex) => (
                                    <div key={weekIndex} className="grid grid-cols-7 gap-1">
                                        {week.map((day, dayIndex) => {
                                            if (!day) {
                                                return <div key={dayIndex} className="aspect-square"></div>;
                                            }
                                            const dayInfo = getDayInfo(day);
                                            const isGood = dayInfo?.is_good;
                                            const isSelected = selectedDay === day;

                                            return (
                                                <button
                                                    key={dayIndex}
                                                    onClick={() => setSelectedDay(day)}
                                                    className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-all border-2 ${
                                                        isGood 
                                                            ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400' 
                                                            : 'bg-red-50 border-red-200 hover:border-red-400'
                                                    } ${isSelected ? (isGood ? 'border-emerald-500 ring-2 ring-emerald-300' : 'border-red-500 ring-2 ring-red-300') : ''}`}
                                                >
                                                    <span className={`text-lg font-bold ${isGood ? 'text-emerald-700' : 'text-red-700'}`}>
                                                        {day}
                                                    </span>
                                                    <span className={`text-xs font-medium ${isGood ? 'text-emerald-600' : 'text-red-600'}`}>
                                                        {dayInfo?.mansion_chinese}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Selected Day Details */}
                        {selectedDay && (
                            <Card className={`border-2 ${getDayInfo(selectedDay)?.is_good ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getDayInfo(selectedDay)?.is_good ? 'bg-emerald-500' : 'bg-red-500'}`}>
                                            {getDayInfo(selectedDay)?.is_good ? (
                                                <Check className="w-6 h-6 text-white" />
                                            ) : (
                                                <X className="w-6 h-6 text-white" />
                                            )}
                                        </div>
                                        <div>
                                            <div className={`text-xl font-bold ${getDayInfo(selectedDay)?.is_good ? 'text-emerald-800' : 'text-red-800'}`}>
                                                {getDayInfo(selectedDay)?.result} - {MONTH_NAMES[month - 1]} {selectedDay}
                                            </div>
                                            <div className="text-sm text-slate-500">
                                                {getDayInfo(selectedDay)?.weekday} • {getDayInfo(selectedDay)?.mansion_chinese} ({getDayInfo(selectedDay)?.mansion_english})
                                            </div>
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className={`text-base leading-relaxed ${getDayInfo(selectedDay)?.is_good ? 'text-emerald-700' : 'text-red-700'}`}>
                                        {getDayInfo(selectedDay)?.meaning}
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Good Days Summary */}
                        <div className="mt-6 text-center">
                            <p className="text-slate-600">
                                <span className="font-semibold text-emerald-600">Good days this month:</span>{' '}
                                {data?.good_days?.join(', ')}
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
